// @ts-nocheck
// import { getForceLogoutFlag, getTokenContext, removeSyncingState, shouldSkipSync, syncWithLiveTokenContext } from "./pw-sync-utils.js";

/**
 * pwlive-syncup.js
 * ----------------------------------------------------------------------------
 * Entry point loaded on PW-Live pages. Runs immediately (IIFE) to decide
 * whether the Shopify session needs to be re-synced against the PW-Live auth
 * session (via the multipass endpoint), then either skips (clearing sync
 * state) or performs the sync and redirects.
 *
 * Note: the `import` above is commented out — this file instead inlines its
 * own copy of the pw-sync-utils.js helpers (getCookie, safeJSONParse,
 * toBoolean, getFromAppFlag, getForceLogoutFlag, forceLogout,
 * removeSyncingState, syncWithLiveTokenContext, getTokenContext,
 * getUserIdFromToken, shouldSkipSync) further down in this same file, rather
 * than importing the shared module. They are `function` declarations, so
 * they are hoisted and available to the IIFE below even though they are
 * defined later in the file. See pw-sync-utils.js for the documented
 * behavior of each helper — this copy should be kept in sync with it.
 *
 * Function flow (runs immediately on script load)
 * ----------------------------------------------------------------------------
 *   syncTokenContext()
 *     |-- getForceLogoutFlag() / getTokenContext() -> { stored, live, redirectUrl }
 *     |-- resolveRedirectUrl(redirectUrl)
 *     |     -- if redirectUrl is empty or "/": prefer localStorage
 *     |        "preferences".redirection_url, else "selectedExamRedirectUrl",
 *     |        else fall back to the original redirectUrl
 *     |
 *     '-- shouldSkipSync({ storedContext: stored, liveTokenContext: live })
 *           |-- true  -> track the redirect, then removeSyncingState({ shouldRedirect: true })
 *           '-- false -> syncWithLiveTokenContext({ ...live, redirectUrl: finalRedirectUrl })
 *                          -> POST /auth/multipass; on success persists
 *                             STORE_TOKEN_CONTEXT/preferences and redirects,
 *                             on failure calls forceLogout() and redirects.
 */
(function () {
  "use strict";

  function resolveRedirectUrl(redirectUrl) {
    // If redirect is home page, override with localStorage (preferences or selectedExamRedirectUrl)
    if (!redirectUrl || redirectUrl === "/") {
      try {
        const prefsRaw = localStorage.getItem("preferences");
        if (prefsRaw) {
          const prefs = JSON.parse(prefsRaw);
          if (prefs.redirection_url) {
            console.log("[PW Sync] Using preferences redirection_url:", prefs.redirection_url);
            return prefs.redirection_url;
          }
        }
        const storedRedirect = localStorage.getItem("selectedExamRedirectUrl");
        if (storedRedirect) {
          console.log("[PW Sync] Using localStorage redirect:", storedRedirect);
          return storedRedirect;
        }
      } catch (e) {}
    }
    return redirectUrl;
  }

  async function syncTokenContext() {
    console.log("[PW Sync] Running syncTokenContext pw-live 1");

    const navigation = performance.getEntriesByType("navigation")[0];
    const isPageRefresh = navigation?.type === "reload";
    const isForceLogout = getForceLogoutFlag();
    const tokenContext = getTokenContext();
    const { stored, live, redirectUrl } = tokenContext;
    // 🔥 Apply redirect override logic here
    const finalRedirectUrl = resolveRedirectUrl(redirectUrl);
    if (shouldSkipSync({ storedContext: stored, liveTokenContext: live })) {
      const authEvents = window.PWAuthenticateEvents;
      const finishSkip = function () {
        removeSyncingState({
          shouldRedirect: true,
          redirectUrl: finalRedirectUrl,
        });
      };
      if (authEvents && typeof authEvents.trackRedirect === "function") {
        authEvents
          .trackRedirect({
            token: live.token,
            landingPage: finalRedirectUrl,
            status: "success",
          })
          .finally(finishSkip);
      } else {
        finishSkip();
      }
      return;
    }
    syncWithLiveTokenContext({
      ...live,
      redirectUrl: finalRedirectUrl,
    });
  }

  syncTokenContext();
  console.log("[PW Live Sync] Script initialized");
})();

// @ts-nocheck
/**
 * PW Sync Utilities - Shared helpers for authentication sync
 */

/* -------------------- Cookie & Storage Helpers -------------------- */

export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  return parts.length === 2 ? parts.pop().split(";").shift() : null;
}

export function safeJSONParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function toBoolean(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "boolean") return value;
  return value === "true";
}

/* -------------------- Auth State Helpers -------------------- */

export function getFromAppFlag() {
  const params = new URLSearchParams(window.location.search);
  return toBoolean(
    params.get("from_app") ??
    params.get("fromApp") ??
    params.get("from_App") ??
    sessionStorage.getItem("fromApp")
  );
}

export function getForceLogoutFlag() {
  return toBoolean(
    sessionStorage.getItem("force_logout") ??
    localStorage.getItem("force_logout")
  ) || false;
}

/* -------------------- Cleanup & Logout -------------------- */

export function forceLogout(reason = "unknown") {
  console.error("[PW Sync] Forcing logout:", reason);

  // Local storage cleanup
  localStorage.removeItem("STORE_TOKEN_CONTEXT");
  localStorage.removeItem("preferences");
  localStorage.removeItem("pw-sync-token");
  localStorage.removeItem("pw-sync-random-id");
  localStorage.removeItem("selectedExamName");
  localStorage.removeItem("selectedExamIcon");
  localStorage.removeItem("selectedExamRedirectUrl");
  
  // Keep auth failed flag during logout to prevent retry, 
  // but set a flag to clear it after logout completes
  sessionStorage.setItem("pw_logout_in_progress", "true");

  // CookieStore API (SAFE)
  if ("cookieStore" in window) {
    cookieStore.delete("STORE_TOKEN_CONTEXT");
    cookieStore.delete("preferences");
  }

  // Redirect AFTER cleanup
  // window.location.replace("/account/logout");  
}

export function removeSyncingState(options = {}) {
  const { shouldRedirect = false, redirectUrl = "/" } = options;

  localStorage.removeItem('pw-sync-token');
  localStorage.removeItem('pw-sync-random-id');

  const isAuthenticatePage = window.location.pathname === "/pages/authenticate";
  if (!isAuthenticatePage) {
    const loaderSection = document.querySelector('.loader-section');
    if (loaderSection) {
      loaderSection.classList.add('fade-out');
      setTimeout(() => {
        loaderSection.style.display = 'none';
      }, 300);
    }
  }

  if (shouldRedirect) {
    window.location.replace(redirectUrl);
  } 
}

/* -------------------- API Sync -------------------- */

export async function syncWithLiveTokenContext({ token, randomId, redirectUrl }) {
  if (!token || !randomId) return;
  console.log("[PW Sync] Sending redirectUrl to backend:", redirectUrl);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  const authEvents = window.PWAuthenticateEvents || {};
  const trackRedirect =
    typeof authEvents.trackRedirect === "function"
      ? authEvents.trackRedirect.bind(authEvents)
      : async function () {};
  const fromPage =
    typeof authEvents.getFromPage === "function" ? authEvents.getFromPage() : "/pages/authenticate";
  const landingPage = redirectUrl || "/";

  try {
    const response = await fetch(`${window.PW_API_BASE || "/apps/pw_module/api"}/auth/multipass`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        randomId,
        redirectUrl,
        fromApp: getFromAppFlag(),
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.status === 401 || response.status === 403 || response.status === 400) {
      console.error("[PW Sync] Authentication failed:", response.status);
      sessionStorage.setItem("pw_auth_failed", "true");
      const failMessage = "Authentication failed: " + response.status;
      await trackRedirect({
        token,
        fromPage,
        landingPage,
        status: "failure",
        errorMessage: failMessage,
      });
      forceLogout(failMessage);
      window.location.replace(redirectUrl);
      return;
    }

    const data = await response.json();
    console.log("[PW Sync] Backend redirect_url:", data?.data?.redirect_url);

    if (!response.ok || data.status !== "success") {
      throw new Error("Multipass sync failed with status: " + response.status);
    }

    sessionStorage.removeItem("pw_auth_failed");

    localStorage.setItem("STORE_TOKEN_CONTEXT", JSON.stringify({ token, randomId }));

    sessionStorage.removeItem("force_logout");
    sessionStorage.setItem("pw_last_sync_time", Date.now().toString());

    const preferences = data?.data?.preferences;
    const exams = preferences?.exams;
    const cohort = exams?.[exams.length - 1];
    const selectedExamName = cohort ? String(cohort).split("-").join(" ").trim() : "";
    const examIcon = preferences?.examIcon || "";
    const redirectionUrl = preferences?.redirection_url || data?.data?.redirect_url || "/";
    const { pw_uuid: pwUserId, cohortId, phone, email , firstName, lastName , name} = data?.data;
    sessionStorage.setItem("user_data", JSON.stringify({ pwUserId, cohortId, phone, email , firstName, lastName , name }));
    // Check if user has changed cohort in this session - if so, preserve their selection
    const cohortChangedInSession = sessionStorage.getItem("cohortChangedInSession") === "true";

    if (preferences) {
      localStorage.setItem("preferences", JSON.stringify(preferences));
    }
    if (!cohortChangedInSession && (cohort || selectedExamName || examIcon)) {
      localStorage.setItem("selectedExamName", selectedExamName);
      localStorage.setItem("selectedExamIcon", examIcon);
      localStorage.setItem("selectedExamRedirectUrl", redirectionUrl);
    }

    localStorage.removeItem("pw-sync-token");
    localStorage.removeItem("pw-sync-random-id");
    sessionStorage.removeItem("force_logout");

    // await trackRedirect({
    //   token,
    //   fromPage,
    //   landingPage,
    //   status: "success",
    // });

    if (typeof window.markCartMigratePending === "function") {
      window.markCartMigratePending();
    } else {
      try {
        sessionStorage.setItem("pw-cart-migrate-pending", "1");
      } catch (e) {}
    }

    window.location.replace(data?.data?.redirect_url);
  } catch (err) {
    clearTimeout(timeoutId);

    let failMessage = "Sync error: " + (err && err.message ? err.message : String(err));
    if (err && err.name === "AbortError") {
      console.error("[PW Sync] API timeout after 10 seconds");
      sessionStorage.setItem("pw_auth_failed", "true");
      failMessage = "Multipass sync timed out after 10 seconds";
    } else {
      console.error("[PW Sync Error]", err);
    }

    await trackRedirect({
      token,
      fromPage,
      landingPage,
      status: "failure",
      errorMessage: failMessage,
    });
    forceLogout(failMessage);
    window.location.replace(redirectUrl);
  }
}

/* -------------------- Token Context Logic -------------------- */

export function getTokenContext() {
  console.log("for geting token context");
  const params = new URLSearchParams(window.location.search);
  if (
    params.has("from_app") ||
    params.has("fromApp") ||
    params.has("from_App")
  ) {
    if (sessionStorage.getItem("fromApp") !== "true") {
      sessionStorage.setItem("fromApp", "true");
    }
  }

  const appVersion = params.get("APP_VERSION");
  if (appVersion) {
    const parsed = parseInt(String(appVersion), 10);
    if (Number.isFinite(parsed)) {
      sessionStorage.setItem("app_version_name", String(parsed));
    }
  }

  const hash = window.location.hash;
  const redirectUrl = decodeURIComponent(params.get("redirect_url") || "/");
  const fullRedirectUrl = redirectUrl.includes('#') ? redirectUrl : `${redirectUrl}${hash}`;
  let filteredRedirectUrl = fullRedirectUrl.replace(/^https?:\/\/.*?\.live/, "");

  if (!filteredRedirectUrl.startsWith("/")) {
    filteredRedirectUrl = "/" + filteredRedirectUrl;
  }
  console.log("[PW Sync] Computed redirectUrl:", {
    raw: redirectUrl,
    hash,
    full: fullRedirectUrl,
    filtered: filteredRedirectUrl,
  });
  const storedContext = safeJSONParse(
    localStorage.getItem("STORE_TOKEN_CONTEXT")
  );
  const cookieContext = safeJSONParse(getCookie("TOKEN_CONTEXT"));

  return {
    stored: storedContext,
    cookie: cookieContext,
    live: {
      token: localStorage.getItem('pw-sync-token') || cookieContext?.token,
      randomId: localStorage.getItem('pw-sync-random-id') || cookieContext?.randomId,
    },
    redirectUrl: filteredRedirectUrl,
  };
}

export function getUserIdFromToken(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.data?._id ?? null;
  } catch {
    return null;
  }
}

export function shouldSkipSync({ storedContext, liveTokenContext }) {

  console.log("runnning should sync function"); 
  
  const storedUserId = getUserIdFromToken(storedContext?.token);
  const liveUserId = getUserIdFromToken(liveTokenContext?.token);
  //this will print store user id
  console.log("printing store user id",storedUserId);

  //this print live user id
  console.log("printing live user id",liveUserId);
  
  // Never throttle an unauthenticated user who has a token — they must always
  // reach the multipass endpoint to get a Shopify session, even on a retry
  // within the 10-second window (e.g. page reload, Shopify redirect-back).
  
  if (!window.hasCustomer && liveTokenContext.token && liveTokenContext.randomId) {
    return false;
  }
  
  // Throttle: skip if we recently synced (10s buffer) — only applies when
  // the user is already authenticated (hasCustomer === true).
  const lastSyncTime = parseInt(sessionStorage.getItem('pw_last_sync_time') || '0', 10);
  
  if (Date.now() - lastSyncTime < 10000) {
      console.log("[PW Sync] Recently synced — skipping");
      return true;
  }
  
  if (!liveTokenContext.token || !liveTokenContext.randomId) {
    console.log("[PW Sync] No valid token context found");
    return true;
  }
  console.log("printing from line 282"); 
  if (storedContext?.randomId && storedContext?.token ) {
    if (storedContext.token === liveTokenContext.token) {
      console.log("[PW Sync] Token context unchanged between pw store and pw live");
      return true;
    }  
  
    // Same randomId but different token — only skip if the logged-in user hasn't changed
  
    if (storedUserId && liveUserId && storedUserId === liveUserId) {
      console.log("[PW Sync] Same user (user_id unchanged), token refreshed — skipping sync");
      const tokenContext = {
        token: liveTokenContext?.token,
        randomId: liveTokenContext?.randomId,
      };
      localStorage.setItem("STORE_TOKEN_CONTEXT", JSON.stringify(tokenContext));
      return true;
    }
    console.log("[PW Sync] Same randomId but different user_id — proceeding with sync");
  }

  return false;
}

